/**
 * PremiumCharts.js
 *
 * SVG chart primitives of the premium design kit:
 *  - {@link Sparkline}   — smooth line/area trend (hero + KPI cards);
 *  - {@link BubbleChart} — circle-packed distribution (channel / status mix);
 *  - {@link CapsuleBars} — capsule columns with a floating value tooltip.
 *
 * Pure presentation: every value comes in through props, geometry lives in
 * `helpers/premiumChartMath`. Light/dark aware through the management theme.
 *
 * @module components/premium/PremiumCharts
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Animated, Easing, Pressable, StyleSheet, Text, View} from 'react-native';
import Svg, {Circle, Defs, LinearGradient as SvgGradient, Path, Stop} from 'react-native-svg';
import {LinearGradient} from 'expo-linear-gradient';
import {BRAND, useManagementTheme} from '../../helpers/managementTheme';
import {
    areaPathFromLine,
    compactNumber,
    packCircles,
    percentOf,
    seriesToPoints,
    smoothLinePath,
    toNumber,
} from '../../helpers/premiumChartMath';

/** Brand-tint ramp used by the bubble chart, strongest → softest. */
export const BUBBLE_RAMP = ['#F97316', '#FB923C', '#FDBA74', '#FED7AA', '#FFEDD5'];

/** Headroom above the tallest capsule reserved for the floating tooltip (px). */
const TOOLTIP_ROOM = 34;

/** Text colour that stays readable on each {@link BUBBLE_RAMP} step. */
const BUBBLE_TEXT = ['#FFFFFF', '#FFFFFF', '#7C2D12', '#7C2D12', '#7C2D12'];

/**
 * Smooth trend line with an optional soft area fill.
 *
 * @param {object} props
 * @param {Array<number|{value: number}>} props.data Series values.
 * @param {string} [props.color] Stroke colour (defaults to the brand orange).
 * @param {number} [props.height=56] Chart height in px.
 * @param {boolean} [props.fill=true] Paint the gradient area under the line.
 * @param {number} [props.strokeWidth=2.5] Stroke width in px.
 * @param {boolean} [props.dot=true] Draw a marker on the last point.
 * @param {string} [props.dotRing] Ring colour around the marker (card surface colour).
 * @returns {JSX.Element} The sparkline (an empty spacer when there is no series).
 */
export const Sparkline = ({data, color = BRAND.base, height = 56, fill = true, strokeWidth = 2.5, dot = true, dotRing = '#FFFFFF'}) => {
    const [width, setWidth] = useState(0);
    const gradientId = useMemo(() => `spark-${String(color).replace(/[^a-zA-Z0-9]/g, '')}-${Math.round(height)}`, [color, height]);
    const geometry = useMemo(() => {
        if (!width) {
            return null;
        }

        const points = seriesToPoints(data, width, height, strokeWidth + 2);
        const line = smoothLinePath(points);

        return {points, line, area: areaPathFromLine(line, points, height)};
    }, [data, width, height, strokeWidth]);

    const onLayout = useCallback((event) => setWidth(Math.round(event.nativeEvent.layout.width)), []);
    const last = geometry?.points?.[geometry.points.length - 1];

    return (
        <View style={{height}} onLayout={onLayout}>
            {!!geometry?.line && (
                <Svg width={width} height={height}>
                    <Defs>
                        <SvgGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <Stop offset="0" stopColor={color} stopOpacity={0.34} />
                            <Stop offset="1" stopColor={color} stopOpacity={0} />
                        </SvgGradient>
                    </Defs>
                    {fill && !!geometry.area && <Path d={geometry.area} fill={`url(#${gradientId})`} />}
                    <Path d={geometry.line} stroke={color} strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    {dot && !!last && (
                        <>
                            <Circle cx={Math.min(last.x, width - 5)} cy={last.y} r={5.5} fill={dotRing} />
                            <Circle cx={Math.min(last.x, width - 5)} cy={last.y} r={3.2} fill={color} />
                        </>
                    )}
                </Svg>
            )}
        </View>
    );
};

/**
 * Circle-packed distribution chart (area ∝ value) with percent labels inside the
 * bubbles and a legend underneath. Tapping a bubble or legend row selects it.
 *
 * @param {object} props
 * @param {Array<{label: string, value: number, color?: string}>} props.data Segments.
 * @param {number} [props.height=220] Plot height in px.
 * @param {Function} [props.valueFormat] `(value) => string` for the legend / selection line.
 * @param {string} [props.emptyText] Shown when no segment has a positive value.
 * @returns {JSX.Element} The chart.
 */
export const BubbleChart = ({data, height = 220, valueFormat, emptyText}) => {
    const m = useManagementTheme();
    const [width, setWidth] = useState(0);
    const [selected, setSelected] = useState(null);
    const items = useMemo(() => (Array.isArray(data) ? data : []).filter((d) => toNumber(d?.value) > 0), [data]);
    const total = useMemo(() => items.reduce((sum, d) => sum + toNumber(d.value), 0), [items]);
    const bubbles = useMemo(
        () => (width ? packCircles(items.map((d) => d.value), {width, height, gap: 6}) : []),
        [items, width, height],
    );
    // Rank (0 = biggest) decides the colour step, independent of input order.
    const rankOf = useMemo(() => {
        const order = items.map((d, index) => ({index, value: toNumber(d.value)})).sort((a, b) => b.value - a.value);

        return Object.fromEntries(order.map((entry, rank) => [entry.index, rank]));
    }, [items]);

    useEffect(() => {
        setSelected(null);
    }, [data]);

    if (!items.length) {
        return (
            <View style={styles.emptyWrap}>
                <Text style={[styles.emptyText, {color: m.colors.subText}]}>{emptyText || '—'}</Text>
            </View>
        );
    }

    const colorFor = (index) => items[index]?.color || BUBBLE_RAMP[Math.min(rankOf[index], BUBBLE_RAMP.length - 1)];
    const textFor = (index) => (items[index]?.color ? '#FFFFFF' : BUBBLE_TEXT[Math.min(rankOf[index], BUBBLE_TEXT.length - 1)]);
    const active = selected != null ? items[selected] : null;

    return (
        <View>
            <View style={{height}} onLayout={(event) => setWidth(Math.round(event.nativeEvent.layout.width))}>
                {bubbles.map((bubble) => {
                    const isActive = selected === bubble.index;
                    const dimmed = selected != null && !isActive;
                    const pct = percentOf(items[bubble.index].value, total);

                    return (
                        <Pressable
                            key={`${items[bubble.index].label}-${bubble.index}`}
                            accessibilityRole="button"
                            accessibilityLabel={`${items[bubble.index].label} ${pct}%`}
                            onPress={() => setSelected(isActive ? null : bubble.index)}
                            style={[
                                styles.bubble,
                                {
                                    left: bubble.x - bubble.r,
                                    top: bubble.y - bubble.r,
                                    width: bubble.r * 2,
                                    height: bubble.r * 2,
                                    borderRadius: bubble.r,
                                    backgroundColor: colorFor(bubble.index),
                                    opacity: dimmed ? 0.45 : 1,
                                    transform: [{scale: isActive ? 1.04 : 1}],
                                },
                            ]}
                        >
                            {bubble.r >= 20 && (
                                <Text
                                    allowFontScaling={false}
                                    style={[styles.bubbleText, {color: textFor(bubble.index), fontSize: Math.max(11, Math.min(34, bubble.r * 0.52))}]}
                                    numberOfLines={1}
                                >
                                    {pct}%
                                </Text>
                            )}
                        </Pressable>
                    );
                })}
            </View>

            {!!active && (
                <Text style={[styles.selection, {color: m.colors.heading}]} numberOfLines={1}>
                    {active.label}: {valueFormat ? valueFormat(active.value) : active.value} · {percentOf(active.value, total)}%
                </Text>
            )}

            <View style={styles.legend}>
                {items.map((d, index) => (
                    <Pressable
                        key={`${d.label}-leg-${index}`}
                        style={styles.legendItem}
                        onPress={() => setSelected(selected === index ? null : index)}
                    >
                        <View style={[styles.legendDot, {backgroundColor: colorFor(index)}]} />
                        <Text style={[styles.legendLabel, {color: selected === index ? m.colors.heading : m.colors.subText, fontWeight: selected === index ? '800' : '600'}]} numberOfLines={1}>
                            {d.label}
                        </Text>
                    </Pressable>
                ))}
            </View>
        </View>
    );
};

/**
 * Capsule columns (rounded track + brand-orange fill) with a floating dark
 * tooltip on the selected column. Replaces the flat bars on trend charts.
 *
 * @param {object} props
 * @param {Array<{label: string, fullLabel?: string, value: number}>} props.data Columns, oldest → newest.
 * @param {number} [props.height=190] Plot height in px (labels excluded).
 * @param {Function} [props.valueFormat] `(value) => string` for the tooltip.
 * @param {Function} [props.onSelect] `(item, index) => void` after a tap.
 * @param {string} [props.emptyText] Shown for an empty series.
 * @returns {JSX.Element} The chart.
 */
export const CapsuleBars = ({data = [], height = 190, valueFormat, onSelect, emptyText}) => {
    const m = useManagementTheme();
    const count = data.length;
    const max = useMemo(() => Math.max(1, ...data.map((d) => toNumber(d.value))), [data]);
    const [selected, setSelected] = useState(count ? count - 1 : 0);
    const reveal = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        setSelected(data.length ? data.length - 1 : 0);
        reveal.setValue(0);
        Animated.timing(reveal, {toValue: 1, duration: 700, easing: Easing.out(Easing.cubic), useNativeDriver: false}).start();
    }, [data, reveal]);

    if (!count) {
        return (
            <View style={styles.emptyWrap}>
                <Text style={[styles.emptyText, {color: m.colors.subText}]}>{emptyText || '—'}</Text>
            </View>
        );
    }

    // Dense series (a month of days) would collide labels — show every n-th.
    const labelEvery = count > 16 ? Math.ceil(count / 8) : 1;
    const capsuleMax = count > 16 ? 12 : count > 10 ? 20 : 30;
    const tooltipText = valueFormat ? valueFormat(data[selected]?.value || 0) : compactNumber(data[selected]?.value || 0);

    return (
        <View>
            <View style={[styles.capsuleArea, {height: height + TOOLTIP_ROOM}]}>
                {data.map((d, index) => {
                    const isActive = index === selected;
                    const ratio = Math.max(0.1, toNumber(d.value) / max);
                    const fillHeight = reveal.interpolate({inputRange: [0, 1], outputRange: [0, Math.round(height * ratio)]});
                    const anchor = index < 2 ? 'left' : index > count - 3 ? 'right' : 'center';

                    return (
                        <Pressable
                            key={`${d.label}-${index}`}
                            style={styles.capsuleColumn}
                            accessibilityRole="button"
                            accessibilityLabel={`${d.fullLabel || d.label}`}
                            onPress={() => {
                                setSelected(index);
                                onSelect?.(d, index);
                            }}
                        >
                            <View style={[styles.capsuleTrack, {maxWidth: capsuleMax, backgroundColor: m.colors.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(249,115,22,0.07)'}]}>
                                <Animated.View style={[styles.capsuleFill, {height: fillHeight, opacity: isActive || m.colors.isDark ? 1 : 0.5}]}>
                                    {isActive ? (
                                        <LinearGradient colors={['#FB923C', '#F97316']} start={{x: 0, y: 0}} end={{x: 0, y: 1}} style={StyleSheet.absoluteFill} />
                                    ) : (
                                        <View style={[StyleSheet.absoluteFill, {backgroundColor: m.colors.isDark ? 'rgba(251,146,60,0.42)' : '#FDBA74'}]} />
                                    )}
                                    {isActive && <View style={[styles.capsuleDot, {borderColor: m.colors.surface}]} />}
                                </Animated.View>
                            </View>
                            {isActive && (
                                <Animated.View
                                    pointerEvents="none"
                                    style={[
                                        styles.tooltipWrap,
                                        {bottom: Math.round(height * ratio) + 14},
                                        anchor === 'left' && {left: 0, alignItems: 'flex-start'},
                                        anchor === 'right' && {right: 0, alignItems: 'flex-end'},
                                        anchor === 'center' && {left: -48, right: -48, alignItems: 'center'},
                                    ]}
                                >
                                    <View style={styles.tooltip}>
                                        <Text style={styles.tooltipText} numberOfLines={1}>{tooltipText}</Text>
                                    </View>
                                </Animated.View>
                            )}
                        </Pressable>
                    );
                })}
            </View>
            <View style={styles.labelRow}>
                {data.map((d, index) => (
                    <Text
                        key={`${d.label}-lbl-${index}`}
                        style={[styles.capsuleLabel, {color: index === selected ? m.colors.primary : m.colors.muted, fontWeight: index === selected ? '800' : '600'}]}
                        numberOfLines={1}
                    >
                        {index % labelEvery === 0 || index === selected ? d.label : ''}
                    </Text>
                ))}
            </View>
            {!!data[selected]?.fullLabel && (
                <Text style={[styles.capsuleCaption, {color: m.colors.subText}]} numberOfLines={1}>{data[selected].fullLabel}</Text>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    emptyWrap: {alignItems: 'center', justifyContent: 'center', paddingVertical: 28},
    emptyText: {fontSize: 12.5, fontWeight: '600', textAlign: 'center'},

    bubble: {position: 'absolute', alignItems: 'center', justifyContent: 'center'},
    bubbleText: {fontWeight: '800', letterSpacing: -0.4},
    selection: {fontSize: 13.5, fontWeight: '800', textAlign: 'center', marginTop: 10},
    legend: {flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 6, marginTop: 14},
    legendItem: {flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 4, maxWidth: '100%'},
    legendDot: {width: 9, height: 9, borderRadius: 5, marginRight: 6},
    legendLabel: {fontSize: 12.5, flexShrink: 1},

    capsuleArea: {flexDirection: 'row', alignItems: 'flex-end', paddingTop: TOOLTIP_ROOM},
    capsuleColumn: {flex: 1, height: '100%', alignItems: 'center', justifyContent: 'flex-end'},
    capsuleTrack: {width: '72%', height: '100%', borderRadius: 999, justifyContent: 'flex-end', overflow: 'hidden'},
    capsuleFill: {width: '100%', borderRadius: 999, overflow: 'hidden'},
    capsuleDot: {position: 'absolute', top: 5, alignSelf: 'center', width: 11, height: 11, borderRadius: 6, backgroundColor: '#FFFFFF', borderWidth: 0},
    tooltipWrap: {position: 'absolute'},
    tooltip: {backgroundColor: '#12141C', borderRadius: 999, paddingHorizontal: 11, paddingVertical: 5},
    tooltipText: {color: '#FFFFFF', fontSize: 11.5, fontWeight: '800'},
    labelRow: {flexDirection: 'row', marginTop: 8},
    capsuleLabel: {flex: 1, textAlign: 'center', fontSize: 10.5},
    capsuleCaption: {fontSize: 12, fontWeight: '600', marginTop: 8, textAlign: 'center'},
});
